import { createReducer } from 'typesafe-actions'
import { ReadCommissionsRequestDto, ReadCommissionsResponseDto } from '@sparling/financial-common'
import { omit, isEmpty } from 'lodash'
import { ActionTypes } from '../types'
import { LoadingStateEnum } from '../../types'
import {
  getCommissionsRequest,
  getCommissionsSuccess,
  getCommissionsFailure,
  openApproveCommissionsModal,
  openRejectCommissionsModal,
  openAdjustCommissionsModal,
  setAdjustCommissions,
  openAdjustConfirmCommissionsModal,
  updateCommissionsSuccess,
  approveCommissionsSuccess,
  rejectCommissionsSuccess,
  resetCommissionsFilters,
} from '../actions'

type HandleModalData = {
  open: boolean
  ids: number[]
}

export type CommissionsState = {
  approveCommissionsModal: HandleModalData
  adjustCommissionsModal: {
    open: boolean
    percentage: number | null
    amount: number | null
    ids: { [key: number]: number }
  }
  adjustConfirmCommissionsModal: { open: boolean }
  rejectCommissionsModal: HandleModalData
  loading: LoadingStateEnum
  data: ReadCommissionsResponseDto
  filters: ReadCommissionsRequestDto
  error: Error | null
}

export const initialState = {
  approveCommissionsModal: {
    open: false,
    ids: [],
  },
  adjustCommissionsModal: {
    open: false,
    percentage: null,
    amount: null,
    ids: {},
  },
  adjustConfirmCommissionsModal: {
    open: false,
  },
  rejectCommissionsModal: {
    open: false,
    ids: [],
  },
  loading: LoadingStateEnum.IDLE,
  data: {
    items: [],
    meta: {
      itemCount: 0,
      totalItems: 0,
      itemsPerPage: 0,
      totalPages: 0,
      currentPage: 0,
    },
  },
  filters: {},
  error: null,
}

export const commissions = createReducer<CommissionsState, ActionTypes>(initialState)
  .handleAction(getCommissionsRequest, (state, { payload }) => ({
    ...state,
    filters: {
      ...state.filters,
      ...payload,
      page: Object.keys(omit(payload, 'page')).length > 0 ? 1 : payload.page,
    },
    loading: LoadingStateEnum.LOADING,
  }))
  .handleAction(getCommissionsSuccess, (state, { payload }) => ({
    ...state,
    loading: LoadingStateEnum.SUCCESS,
    data: payload,
  }))
  .handleAction(getCommissionsFailure, (state, { payload }) => ({
    ...state,
    loading: LoadingStateEnum.ERROR,
    data: initialState.data,
    error: payload,
  }))
  .handleAction(openApproveCommissionsModal, (state, { payload: { open, ids } }) => ({
    ...state,
    approveCommissionsModal: {
      ...state.approveCommissionsModal,
      open,
      ids,
    },
    adjustCommissionsModal: initialState.adjustCommissionsModal,
    adjustConfirmCommissionsModal: initialState.adjustConfirmCommissionsModal,
  }))
  .handleAction(openAdjustCommissionsModal, (state, { payload: { open, ids } }) => {
    const currentId = state.approveCommissionsModal.ids[0]
    const currentCommission = state.data.items.find((item) => item.id === currentId)
    return {
      ...state,
      adjustCommissionsModal: {
        ...state.adjustCommissionsModal,
        open,
        ids,
        ...(isEmpty(ids) &&
          open &&
          currentCommission && {
            ids: {
              [currentId]: currentCommission.amount,
            },
          }),
      },
      approveCommissionsModal: {
        ...state.approveCommissionsModal,
        open: false,
      },
    }
  })
  .handleAction(setAdjustCommissions, (state, { payload: { percentage, amount } }) => ({
    ...state,
    adjustCommissionsModal: {
      ...state.adjustCommissionsModal,
      open: false,
      percentage,
      amount,
    },
    adjustConfirmCommissionsModal: {
      open: true,
    },
  }))
  .handleAction(openAdjustConfirmCommissionsModal, (state, { payload: { open } }) => ({
    ...state,
    adjustConfirmCommissionsModal: {
      open,
    },
  }))
  .handleAction(openRejectCommissionsModal, (state, { payload: { open, ids } }) => ({
    ...state,
    rejectCommissionsModal: {
      ...state.rejectCommissionsModal,
      open,
      ids,
    },
  }))
  .handleAction(approveCommissionsSuccess, (state) => ({
    ...state,
    approveCommissionsModal: initialState.approveCommissionsModal,
  }))
  .handleAction(updateCommissionsSuccess, (state) => ({
    ...state,
    approveCommissionsModal: initialState.approveCommissionsModal,
    adjustCommissionsModal: initialState.adjustCommissionsModal,
    adjustConfirmCommissionsModal: initialState.adjustConfirmCommissionsModal,
  }))
  .handleAction(rejectCommissionsSuccess, (state) => ({
    ...state,
    rejectCommissionsModal: initialState.rejectCommissionsModal,
  }))
  .handleAction(resetCommissionsFilters, (state) => ({
    ...state,
    filters: initialState.filters,
  }))
